import React, { createContext, useContext, useRef, useState, useEffect, useCallback } from 'react';
import { toast } from "react-toastify";

// Live status of Knowledge Base document uploads (queue + progress), shared by the
// Add New Dataset popup and the Knowledge Base page. One poll of the tenant's uploads
// (all users) every few seconds while something is queued or processing.

const ReferenceDatasetJobContext = createContext();

const POLL_INTERVAL_MS = 4000;
const MAX_CONSECUTIVE_ERRORS = 5;
const ACTIVE_STATUSES = ["QUEUED", "PROCESSING"];

export const useReferenceDatasetJobs = () => {
  const context = useContext(ReferenceDatasetJobContext);
  if (!context) {
    throw new Error('useReferenceDatasetJobs must be used within ReferenceDatasetJobProvider');
  }
  return context;
};

export const ReferenceDatasetJobProvider = ({ children }) => {
  const [jobs, setJobs] = useState([]);
  const [dismissedIds, setDismissedIds] = useState([]);

  const timerRef = useRef(null);
  const fetchingRef = useRef(false);
  const errorCountRef = useRef(0);
  const mountedRef = useRef(true);
  const seenActiveRef = useRef(new Set());   // jobs this session saw queued / processing
  const notifiedRef = useRef(new Set());     // finished jobs already handled
  const refreshCallbacksRef = useRef([]);

  // Knowledge Base page registers how to reload its list and counts
  const registerRefresh = useCallback((callback) => {
    refreshCallbacksRef.current.push(callback);
    return () => {
      refreshCallbacksRef.current = refreshCallbacksRef.current.filter((cb) => cb !== callback);
    };
  }, []);

  const triggerRefresh = useCallback(() => {
    refreshCallbacksRef.current.forEach((callback) => {
      if (typeof callback === 'function') callback();
    });
  }, []);

  const handleFinishedJob = useCallback((job) => {
    if (notifiedRef.current.has(job.job_id)) return;
    notifiedRef.current.add(job.job_id);

    // Finished before this session saw it running (e.g. old failures on page load): no popup
    if (!seenActiveRef.current.has(job.job_id)) return;

    if (job.is_mine) {
      if (job.status === "SUCCESS") {
        toast.success(job.result?.message || `Knowledge Base upload completed (${job.dataset_name})`);
      } else {
        toast.error(job.error || `Knowledge Base upload failed (${job.dataset_name})`);
      }
    }

    // New or updated schemes (from any user) change the list
    if (job.status === "SUCCESS") triggerRefresh();
  }, [triggerRefresh]);

  const fetchJobs = useCallback(async () => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;

    let keepPolling = false;

    try {
      const result = await fetch('/mcv/reference_dataset_jobs');
      const data = await result.json();

      if (data?.status === "SUCCESS" && Array.isArray(data.data) && mountedRef.current) {
        errorCountRef.current = 0;
        setJobs(data.data);

        data.data.forEach((job) => {
          if (ACTIVE_STATUSES.includes(job.status)) {
            seenActiveRef.current.add(job.job_id);
            keepPolling = true;
          } else {
            handleFinishedJob(job);
          }
        });
      }
    } catch (err) {
      errorCountRef.current += 1;
      // Network blip while uploads are running: try again a few times before giving up
      keepPolling = errorCountRef.current < MAX_CONSECUTIVE_ERRORS && seenActiveRef.current.size > 0;
    } finally {
      fetchingRef.current = false;
    }

    clearTimeout(timerRef.current);
    if (keepPolling && mountedRef.current) {
      timerRef.current = setTimeout(fetchJobs, POLL_INTERVAL_MS);
    }
  }, [handleFinishedJob]);

  // Call after an upload is queued (or when the Knowledge Base page opens): reads the queue now
  // and keeps polling while anything is queued or processing
  const refreshJobs = useCallback(() => {
    errorCountRef.current = 0;
    clearTimeout(timerRef.current);
    return fetchJobs();
  }, [fetchJobs]);

  const dismissJob = useCallback((jobId) => {
    setDismissedIds((prev) => [...prev, jobId]);
  }, []);

  // Resume uploads that are already running (page reload / opened later)
  useEffect(() => {
    mountedRef.current = true;
    fetchJobs();

    return () => {
      mountedRef.current = false;
      clearTimeout(timerRef.current);
    };
  }, [fetchJobs]);

  const visibleJobs = jobs.filter(
    (job) => !dismissedIds.includes(job.job_id) && (ACTIVE_STATUSES.includes(job.status) || job.status === "FAILED")
  );

  const value = {
    jobs: visibleJobs,
    hasActiveJobs: jobs.some((job) => ACTIVE_STATUSES.includes(job.status)),
    refreshJobs,
    dismissJob,
    registerRefresh,
  };

  return (
    <ReferenceDatasetJobContext.Provider value={value}>
      {children}
    </ReferenceDatasetJobContext.Provider>
  );
};
